import { useState } from 'react';
import { X, AlertTriangle, Send } from 'lucide-react';


const AdminRejectionModal = ({ isOpen, onClose, user, onConfirmRejection }) => {
  const [observations, setObservations] = useState('');
  const [severity, setSeverity] = useState('minor');
  const [showConfirmation, setShowConfirmation] = useState(false);

  const minCharacters = 20;
  const maxCharacters = 500;
  const remainingChars = maxCharacters - observations?.length;

  const rejectionTemplates = [
    { id: 'illegible', text: 'Comprobante ilegible - La imagen del comprobante de pago no se puede leer claramente. Por favor, suba una imagen de mejor calidad.' },
    { id: 'incorrect_amount', text: 'Valor incorrecto - El monto en el comprobante no coincide con el valor del plan seleccionado. Verifique el pago y suba el comprobante correcto.' },
    { id: 'expired', text: 'Documento vencido - El comprobante de pago está vencido o no corresponde a una transacción reciente.' },
    { id: 'inconsistent', text: 'Información inconsistente - Los datos del comprobante no coinciden con la información de registro proporcionada.' }
  ];

  const severityConfig = {
    minor: { label: 'Problema Menor', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
    major: { label: 'Problema Mayor', color: 'bg-orange-100 text-orange-800 border-orange-300' },
    fraud: { label: 'Sospecha de Fraude', color: 'bg-red-100 text-red-800 border-red-300' }
  };

  const handleTemplateSelect = (template) => {
    setObservations(template?.text);
  };

  const handleSubmit = () => {
    if (observations?.length < minCharacters) {
      alert(`Por favor, escriba al menos ${minCharacters} caracteres.`);
      return;
    }
    setShowConfirmation(true);
  };

  const handleConfirmRejection = () => {
    onConfirmRejection?.({
      userId: user?.id,
      observations,
      severity,
      timestamp: new Date()?.toISOString()
    });
    setShowConfirmation(false);
    setObservations('');
    setSeverity('minor');
    onClose?.();
  };

  const handleCancel = () => {
    setObservations('');
    setSeverity('minor');
    setShowConfirmation(false);
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-orange-500" />
              Rechazar Verificación
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Usuario: <span className="font-semibold">{user?.name}</span> | Plan: <span className="font-semibold">{user?.membershipTier}</span>
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Templates */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Plantillas de Rechazo (Opcional)
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {rejectionTemplates?.map((template) => (
                <button
                  key={template?.id}
                  onClick={() => handleTemplateSelect(template)}
                  className="text-left p-3 border border-gray-300 rounded-lg hover:bg-blue-50 hover:border-blue-400 transition-colors text-sm"
                >
                  {template?.text?.split(' - ')?.[0]}
                </button>
              ))}
            </div>
          </div>

          {/* Observations Text Area */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Observaciones Detalladas *
            </label>
            <textarea
              value={observations}
              onChange={(e) => setObservations(e?.target?.value)}
              placeholder="Describa el motivo del rechazo:\n\nEjemplos:\n- Comprobante ilegible\n- Valor incorrecto según el plan\n- Documento vencido\n- Información inconsistente\n\nIncluya recomendaciones para que el usuario pueda corregir el problema."
              className="w-full h-40 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm"
              maxLength={maxCharacters}
            />
            <div className="flex justify-between items-center mt-2">
              <span className={`text-xs ${observations?.length < minCharacters ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                Mínimo {minCharacters} caracteres {observations?.length < minCharacters && `(faltan ${minCharacters - observations?.length})`}
              </span>
              <span className={`text-xs ${remainingChars < 50 ? 'text-orange-600 font-semibold' : 'text-gray-500'}`}>
                {remainingChars} caracteres restantes
              </span>
            </div>
          </div>

          {/* Severity Level */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Nivel de Severidad
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {Object.entries(severityConfig)?.map(([key, config]) => (
                <button
                  key={key}
                  onClick={() => setSeverity(key)}
                  className={`p-3 border-2 rounded-lg text-sm font-medium transition-all ${
                    severity === key
                      ? config?.color
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {config?.label}
                </button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleCancel}
              className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={observations?.length < minCharacters}
              className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold transition-colors flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" />
              Confirmar Rechazo
            </button>
          </div>
        </div>

        {/* Confirmation Dialog */}
        {showConfirmation && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
              <h3 className="text-lg font-bold text-gray-900 mb-2">¿Confirmar Rechazo?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Esta acción notificará al usuario <span className="font-semibold">{user?.name}</span> sobre el rechazo de su verificación. El usuario recibirá las observaciones y podrá reenviar su documentación.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmRejection}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors"
                >
                  Sí, Rechazar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRejectionModal;